import { Quote } from "../quote.type";

export const agentRequestType = {
  freightRate: "freight_rate",
  originCharges: "origin_charges",
  destinationCharges: "destination_charges",
} as const;

export type AgentRequestType =
  (typeof agentRequestType)[keyof typeof agentRequestType];

export const agentRequestTypeLabel: Record<AgentRequestType, string> = {
  [agentRequestType.freightRate]: "Freight Rate",
  [agentRequestType.originCharges]: "Origin Charges",
  [agentRequestType.destinationCharges]: "Destination Charges",
};

// The list filter groups the three backend types into two.
export const agentRequestGroup = {
  rates: "rates",
  charges: "charges",
} as const;

export type AgentRequestGroup =
  (typeof agentRequestGroup)[keyof typeof agentRequestGroup];

// What a new request is sent for: a Freight tab lane or a Charges tab location.
export interface AgentRequestTarget {
  type: AgentRequestType;
  originLegId: number | null;
  destinationLegId: number | null;
  originName: string | null;
  destinationName: string | null;
  // The charges leg's inland address; always null for a Freight tab lane.
  inlandHaulage: string | null;
}

export interface NewAgentRequest {
  type: AgentRequestType;
  agentIds: number[];
  originLegId: number | null;
  destinationLegId: number | null;
  subject: string;
  message: string;
  attachments: string[];
}

export const agentRequestStatus = {
  requested: "requested",
  closed: "closed",
} as const;

// Fields are public: the shared table reads cells as rowData[column.field].
export class AgentRequest {
  public readonly typeLabel: string;

  constructor(
    public readonly id: number,
    public readonly status: string,
    public readonly type: AgentRequestType,
    public readonly updatedAt: Date | null,
    public readonly sentAt: Date | null,
    public readonly agentName: string | null,
    public readonly clientName: string | null,
    public readonly sentByName: string | null,
    public readonly quoteId: number | null,
    public readonly quoteType: string | null,
    public readonly quoteValidTo: Date | null,
    public readonly originLegId: number | null,
    public readonly originName: string | null,
    public readonly destinationLegId: number | null,
    public readonly destinationName: string | null,
    public readonly quote: Quote | null = null,
    public readonly details: string | null = null,
  ) {
    this.typeLabel = agentRequestTypeLabel[type] ?? type;
  }

  public getId(): number {
    return this.id;
  }

  public getQuoteUrl(): string | null {
    return this.quoteId ? `/quote/${this.quoteId}` : null;
  }
}

export class AgentRequestCount {
  constructor(
    public readonly type: AgentRequestType,
    public readonly originLegId: number | null,
    public readonly destinationLegId: number | null,
    public readonly originLocationId: number | null,
    public readonly destinationLocationId: number | null,
    public readonly count: number,
  ) {}
}

export class AgentRequestCounts {
  constructor(private readonly counts: AgentRequestCount[] = []) {}

  // A Freight tab lane: freight requests sent for exactly this pair of legs.
  public forLane(originLegId: number, destinationLegId: number): number {
    return this.sum(
      (count) =>
        count.type === agentRequestType.freightRate &&
        count.originLegId === originLegId &&
        count.destinationLegId === destinationLegId,
    );
  }

  // A Charges tab location: charges requests sent for a leg at this location.
  public forLocation(locationId: number): number {
    return this.sum(
      (count) =>
        (count.type === agentRequestType.originCharges &&
          count.originLocationId === locationId) ||
        (count.type === agentRequestType.destinationCharges &&
          count.destinationLocationId === locationId),
    );
  }

  private sum(matches: (count: AgentRequestCount) => boolean): number {
    return this.counts
      .filter(matches)
      .reduce((total, count) => total + count.count, 0);
  }
}
